import { useState, useRef } from 'react'
import { Dropdown } from '@components/Dropdown/Dropdown'
import { Checkbox } from '@components/Checkbox/Checkbox'
import { ChevronsUpDown } from 'lucide-react'
import type { EnumOption } from '../types/enum.types'
import { useTranslations } from '../providers/ContactServiceProvider'

interface UseForDropdownProps {
  value: number[]
  options: EnumOption[]
  /** Return true to keep dropdown open, false to close it */
  onChange: (selected: number[]) => boolean
  disabled?: boolean
}

export function UseForDropdown({
  value,
  options,
  onChange,
  disabled = false,
}: UseForDropdownProps) {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)
  const itemClickedRef = useRef(false)

  // Keep a ref to the latest value to avoid stale closures in double-fire scenarios
  const valueRef = useRef(value)
  valueRef.current = value

  // AC4: Auto-hide when only one option or fewer
  if (options.length <= 1) return null

  // AC5: View mode — read-only display
  if (disabled) {
    const selectedLabels = options
      .filter((o) => value.includes(o.value))
      .map((o) => o.label)

    return (
      <div
        className="text-sm text-content-primary dark:text-dark-content-primary"
        aria-label={t.tagSelector.aria.group}
      >
        {selectedLabels.length > 0 ? selectedLabels.join(', ') : '\u2014'}
      </div>
    )
  }

  const handleToggle = (optionValue: number) => {
    itemClickedRef.current = true
    const current = valueRef.current
    const result = current.includes(optionValue)
      ? onChange(current.filter((v) => v !== optionValue))
      : onChange([...current, optionValue])
    if (result === false) itemClickedRef.current = false
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && itemClickedRef.current) {
      itemClickedRef.current = false
      return // item was clicked, keep dropdown open
    }
    setIsOpen(open)
  }

  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label)

  const displayText = selectedLabels.length > 0
    ? selectedLabels.join(', ')
    : t.modal.placeholder.select

  // pointer-events-none on Checkbox prevents the label's programmatic click on the
  // hidden input from bubbling a second click event to the DropdownItem button.
  // The DropdownItem <button> handles all click logic; Checkbox is purely visual.
  //
  // No "select all" entry: this dropdown's real-world use is transferring the single
  // "Predeterminado" classification to one contact (see ContactModal's transfer flow),
  // not bulk-assigning every classification at once — a mass toggle doesn't map to that
  // flow and risked tripping the default-transfer/blocked logic by accident.
  const items = options.map((option) => ({
    children: (
      <Checkbox
        label={option.label}
        checked={value.includes(option.value)}
        onChange={() => {}}
        className="pointer-events-none"
      />
    ),
    onClick: () => handleToggle(option.value),
  }))

  return (
    <div className="block w-full">
      <Dropdown
        // Dropdown only stretches its trigger wrapper to 100% when its OWN `className`
        // contains `w-full` (see Dropdown.tsx's `isFullWidth` check) — the trigger button's
        // own `w-full` below was already there but had no effect, since its `inline-flex`
        // parent (the wrapper Dropdown renders around `renderTrigger`) was sizing to content.
        className="w-full"
        open={isOpen}
        onOpenChange={handleOpenChange}
        items={items}
        ariaLabel={t.tagSelector.aria.group}
        renderTrigger={() => (
          <button
            type="button"
            className="inline-flex items-center justify-between gap-3 w-full px-3 py-2 text-sm font-normal leading-5 rounded-lg border transition-all duration-150 bg-bg-primary border-border-primary text-content-primary dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary hover:border-[#f9f9f9] dark:hover:border-[#3a3a3f] focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary"
          >
            <span className={`truncate ${selectedLabels.length > 0 ? '' : 'text-content-secondary dark:text-content-secondary'}`}>
              {displayText}
            </span>
            <ChevronsUpDown className={`shrink-0 w-4 h-4 transition-transform duration-150 text-content-secondary dark:text-content-secondary ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      />
    </div>
  )
}
